import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  HasPinterestBoardType,
  SocialProfileCheckboxType,
  SocialProfileComponentExtendedType,
  SocialProfileLabelsType,
  SocialProfileView,
  SocialProfileViewProps
} from './SocialProfile.view';
import { checkImageExistence } from '../../../helpers/checkImageExistence';
import {
  CoreSocialProfileFieldsFragment,
  CoreSocialProfileFieldsFragmentDoc,
  GenericProfileType,
  Network,
  SocialOAuthProvider,
  useRefreshSocialProfilePhotoUrlMutation
} from 'models/graphql';
import { useApolloClient } from '@apollo/client';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';

export type SocialProfileControllerProps = SocialProfileLabelsType &
  SocialProfileCheckboxType &
  Omit<
    SocialProfileViewProps,
    'imageExists' | 'getSublabel' | 'showPinterestBoard' | 'showTikTokSettings' | 'setShowTikTokSettings' | 'onCheck'
  > & {
    // controller props
    dontCheckImageExistence?: boolean;
    setCallbackOnWrapperClick?: Dispatch<SetStateAction<{ [key: string]: (checked: boolean) => void }>>;
  };

const isPinterestBoardTypeGuard = (value: Record<string, unknown>): value is HasPinterestBoardType => {
  return (
    value.pinterestBoard === true &&
    typeof value.pinterestFormName === 'string' &&
    (value.pinterestBoardValue === undefined || typeof value.pinterestBoardValue !== 'undefined') && // Replace with the actual type check for FormElementValueType if needed
    typeof value.pinterestRules === 'object' // Adjust this check if RegisterOptions has a more specific structure
  );
};

export const SocialProfileController = (props: SocialProfileControllerProps): JSX.Element => {
  const hasPinterestBoard =
    !!props.checkbox && props.socialProfile.provider === SocialOAuthProvider.PINTEREST && !!props.pinterestBoard;
  const hasTikTokSettings =
    !!props.checkbox && props.socialProfile.provider === SocialOAuthProvider.TIKTOK && !!props.tikTokSettings;
  const [refreshImageMutation] = useRefreshSocialProfilePhotoUrlMutation();
  const [showPinterestBoard, setShowPinterestBoard] = useState<boolean>(hasPinterestBoard && !!props.checked);
  const [showTikTokSettings, setShowTikTokSettings] = useState<boolean>(hasTikTokSettings && !!props.checked);
  const profileValueWatch = props.useFormInstance?.watch(props.useFormName);
  const { cache } = useApolloClient();

  let profileCacheId: string | undefined = undefined;

  // TS has issues with runtime checking, so we make the condition explicit.
  if (props.socialProfile.provider && props.socialProfile.network) {
    profileCacheId = profilesHelpers.getProfileCacheId({
      provider: props.socialProfile.provider,
      id: props.socialProfile.id,
      network: props.socialProfile.network
    });
  }

  const pinterestFormName = isPinterestBoardTypeGuard(props) ? props.pinterestFormName : '';

  const onCheck = useCallback(
    (checked: boolean) => {
      hasTikTokSettings && checked && setShowTikTokSettings(true);
    },
    [hasTikTokSettings, setShowTikTokSettings]
  );

  const { socialProfile, setCallbackOnWrapperClick } = props;

  useEffect(() => {
    if (setCallbackOnWrapperClick) {
      setCallbackOnWrapperClick(prevState => ({ ...prevState, [socialProfile.id]: onCheck }));
    }
  }, [onCheck, socialProfile.id, setCallbackOnWrapperClick]);

  const getIgSubLabel = (socialProfile: SocialProfileComponentExtendedType) => {
    const publishVia =
      socialProfile.provider === SocialOAuthProvider.FACEBOOK ? 'via a Facebook Page' : 'via Instagram.com';

    switch (socialProfile.type) {
      case 'business':
        return `Business Profile (${publishVia})`;
      case 'media_creator':
        return `Creator Profile (${publishVia})`;
      case 'personal':
        return 'Instagram Personal Profile - publish via notification';
      default:
        return 'Instagram Profile';
    }
  };

  const getGenericSubLabel = (socialProfile: SocialProfileComponentExtendedType): string => {
    switch (socialProfile.genericProfile) {
      case GenericProfileType.BLUESKY:
        return 'Bluesky';
      case GenericProfileType.FACEBOOK_GROUP:
        return 'Facebook Group - publish via notification';
      case GenericProfileType.FACEBOOK_PERSONAL:
        return 'Facebook Personal Profile - publish via notification';
      case GenericProfileType.INSTAGRAM_PERSONAL:
        return 'Instagram Personal Profile - publish via notification';
      case GenericProfileType.LINKEDIN_GROUP:
        return 'LinkedIn Group';
      case GenericProfileType.MASTODON:
        return 'Mastodon';
      case GenericProfileType.QUORA:
        return 'Quora';
      case GenericProfileType.REDDIT:
        return 'Reddit';
      case GenericProfileType.SNAPCHAT:
        return 'Snapchat';
      case GenericProfileType.TELEGRAM:
        return 'Telegram';
      case GenericProfileType.WHATSAPP:
        return 'WhatsApp';
      case GenericProfileType.WORDPRESS:
        return 'Wordpress';
      default:
        return '';
    }
  };

  const getSublabel = (socialProfile: SocialProfileComponentExtendedType): string => {
    if (!socialProfile.provider && socialProfile.genericProfile) return getGenericSubLabel(socialProfile);

    switch (socialProfile.provider) {
      case SocialOAuthProvider.TWITTER:
        return 'X (Twitter) Profile';
      case SocialOAuthProvider.TIKTOK:
        return 'TikTok Profile';
      case SocialOAuthProvider.LINKEDIN:
        switch (socialProfile.type) {
          case 'company':
            return 'LinkedIn Company';
          case 'profile':
            return 'LinkedIn Profile';
          default:
            return '';
        }
      case SocialOAuthProvider.PINTEREST:
        return 'Pinterest Profile';
      case SocialOAuthProvider.GOOGLE:
        return 'Google Business Profile';
      case SocialOAuthProvider.FACEBOOK:
        if (socialProfile.network === Network.INSTAGRAM) {
          return getIgSubLabel(socialProfile);
        }

        switch (socialProfile.type) {
          case 'page':
            return 'Facebook Page';
          case 'group':
            return 'Facebook Group';
          default:
            return '';
        }
      case SocialOAuthProvider.INSTAGRAM:
        return getIgSubLabel(socialProfile);
      case SocialOAuthProvider.BITLY:
        return 'Bitly Account';
      default:
        return '';
    }
  };
  const getRefreshedImage = useCallback(
    async (socialProfileId: string) => {
      try {
        const response = await refreshImageMutation({ variables: { socialProfileId } });
        return response.data?.refreshSocialProfilePhotoUrl.photoUrl || '';
      } catch (e) {
        return '';
      }
    },
    [refreshImageMutation]
  );

  // Check if the profile image loads successfully. If it doesn't, try and fetch a new one.
  useEffect(() => {
    if (!profileCacheId || props.image === undefined) {
      return;
    }

    checkImageExistence(props.image).then(imageExists => {
      // Image exists, no need to do anything more.
      if (imageExists) {
        return;
      }

      // We need cache data to continue.
      const cacheData: CoreSocialProfileFieldsFragment | null = cache.readFragment({
        id: profileCacheId,
        fragment: CoreSocialProfileFieldsFragmentDoc
      });
      if (!cacheData) {
        return;
      }

      // Image doesn't exist, but the cache data says it should. Update the cache so it loads the placeholder.
      if (cacheData.photoUrl !== '') {
        cache.writeFragment<CoreSocialProfileFieldsFragment>({
          id: profileCacheId,
          fragment: CoreSocialProfileFieldsFragmentDoc,
          data: {
            ...cacheData,
            photoUrl: ''
          }
        });
      }

      // If we've already fetched the photo for this profile, return.
      // Note - this currently will only ever fetch the photo once per profile per browser session.
      // If we want to fetch it again, we would need to change this to a reactive var.
      if (cacheData.photoUrlFetched) {
        return;
      }

      // Update the cache to make sure no other components for this profile try fetching, and then fetch the image.
      cache.writeFragment<CoreSocialProfileFieldsFragment>({
        id: profileCacheId,
        fragment: CoreSocialProfileFieldsFragmentDoc,
        data: {
          ...cacheData,
          photoUrl: '',
          photoUrlFetched: true
        }
      });
      getRefreshedImage(props.socialProfile.id).then(photoUrl => {
        cache.writeFragment<CoreSocialProfileFieldsFragment>({
          id: profileCacheId,
          fragment: CoreSocialProfileFieldsFragmentDoc,
          data: {
            ...cacheData,
            photoUrl,
            photoUrlFetched: true
          }
        });
      });
    });
  }, [props.image, getRefreshedImage, props.socialProfile.id, cache, profileCacheId]);

  useEffect(() => {
    if (
      props.checkbox &&
      profileValueWatch !== undefined &&
      !!profileValueWatch !== showPinterestBoard &&
      props.socialProfile.provider === SocialOAuthProvider.PINTEREST
    ) {
      if (hasPinterestBoard) {
        setShowPinterestBoard(!!profileValueWatch);
      }

      if (!profileValueWatch && pinterestFormName) {
        props.useFormInstance.unregister(pinterestFormName);
      }
    }
  }, [
    pinterestFormName,
    props.checkbox,
    profileValueWatch,
    props.socialProfile.network,
    hasPinterestBoard,
    props.checked,
    props.useFormInstance,
    showPinterestBoard,
    props.socialProfile.provider
  ]);

  useEffect(() => {
    if (
      props.checkbox &&
      profileValueWatch !== undefined &&
      !profileValueWatch &&
      showTikTokSettings &&
      hasTikTokSettings
    ) {
      setShowTikTokSettings(false);
    }
  }, [props.checkbox, profileValueWatch, showTikTokSettings, hasTikTokSettings]);

  const args: SocialProfileViewProps = {
    ...props,
    image: props.image,
    getSublabel,
    showPinterestBoard,
    showTikTokSettings,
    setShowTikTokSettings,
    onCheck
  };

  return <SocialProfileView {...args} />;
};
